<template>
  <!--
    AB#21624 — the dashboard's "Create Order" control. With Ask ELSA access it's
    a split button that opens a small menu (Manual create · Ask ELSA); without
    it, a plain button that opens the manual form directly — so a merchant who
    can create orders but has no ELSA access never sees an empty dropdown.
    Gated by elsaAllowed() upstream; this component only renders what it's told.
  -->
  <div ref="root" class="co-split" :class="{ 'co-plain': !canElsa }">
    <button
      type="button"
      class="co-main"
      data-test="create-order-main"
      :aria-haspopup="canElsa ? 'menu' : undefined"
      :aria-expanded="canElsa ? open : undefined"
      @click="onMain"
    >
      <PlusCircleIcon class="co-main-ic" />
      <span class="co-main-txt">Create Order</span>
    </button>
    <button
      v-if="canElsa"
      type="button"
      class="co-caret"
      data-test="create-order-caret"
      aria-label="Create order options"
      :aria-expanded="open"
      @click="toggle"
    >
      <ChevronDownIcon class="co-caret-ic" :class="{ 'co-caret-ic--open': open }" />
    </button>

    <div v-if="canElsa && open" class="co-menu" role="menu" data-test="create-order-menu">
      <button
        type="button"
        class="co-mi"
        role="menuitem"
        data-test="create-order-manual"
        @click="pick('manual')"
      >
        <span class="co-mi-ic co-mi-ic--manual"><PencilSquareIcon class="size-[18px]" /></span>
        <span class="co-mi-txt">
          <span class="co-mi-t">Manual create</span>
          <span class="co-mi-d"
            >Fill in the order form yourself — merchant, customer, products.</span
          >
        </span>
      </button>
      <button
        type="button"
        class="co-mi"
        role="menuitem"
        data-test="create-order-elsa"
        @click="pick('elsa')"
      >
        <AgentAvatar agent="elsa" :size="34" />
        <span class="co-mi-txt">
          <span class="co-mi-t">Create with ELSA</span>
          <span class="co-mi-d"
            >Describe the order in plain English and ELSA drafts it for you.</span
          >
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { PlusCircleIcon, ChevronDownIcon, PencilSquareIcon } from '@heroicons/vue/24/outline';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';

const props = defineProps<{
  /** Whether Ask ELSA is available to this user (elsaAllowed upstream). */
  canElsa: boolean;
}>();

const emit = defineEmits<{
  (e: 'manual'): void;
  (e: 'elsa'): void;
}>();

const root = ref<HTMLElement | null>(null);
const open = ref(false);

const toggle = () => {
  open.value = !open.value;
};
// The main button opens the menu when ELSA is offered; otherwise it is the
// single action and goes straight to the manual form.
const onMain = () => {
  if (props.canElsa) toggle();
  else emit('manual');
};
const pick = (which: 'manual' | 'elsa') => {
  open.value = false;
  if (which === 'manual') emit('manual');
  else emit('elsa');
};

const onDocClick = (e: MouseEvent) => {
  if (open.value && root.value && !root.value.contains(e.target as Node)) open.value = false;
};
const onKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape') open.value = false;
};
onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onKey);
});
</script>

<style lang="scss" scoped>
.co-split {
  position: relative;
  display: inline-flex;
  border-radius: 10px;
  box-shadow:
    0 1px 2px rgba(20, 22, 40, 0.28),
    0 6px 16px -6px var(--accent-soft);
}
.co-main {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 40px;
  padding: 0 18px 0 15px;
  border: 0;
  border-radius: 10px 0 0 10px;
  background: var(--accent);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.01em;
  white-space: nowrap;
  line-height: 1;
  cursor: pointer;
}
/* Plain (no ELSA): the main button is the whole control, fully rounded. */
.co-plain .co-main {
  border-radius: 10px;
  padding-right: 18px;
}
.co-main-ic {
  width: 18px;
  height: 18px;
}
.co-caret {
  width: 36px;
  height: 40px;
  border: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0 10px 10px 0;
  background: var(--accent);
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
}
.co-main:hover,
.co-caret:hover {
  background: var(--accent-strong);
}
.co-caret-ic {
  width: 16px;
  height: 16px;
  transition: transform 0.16s ease;
}
.co-caret-ic--open {
  transform: rotate(180deg);
}
.co-menu {
  position: absolute;
  top: 46px;
  right: 0;
  width: 300px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 18px 46px rgba(15, 18, 24, 0.28);
  padding: 6px;
  z-index: 40;
}
.co-mi {
  display: flex;
  gap: 11px;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 10px 11px;
  border-radius: 9px;
  color: var(--ink);
  cursor: pointer;
}
.co-mi:hover {
  background: var(--surface-2);
}
.co-mi-ic {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  flex: none;
  display: grid;
  place-items: center;
}
.co-mi-ic--manual {
  background: var(--accent-soft);
  color: var(--accent);
}
.co-mi-txt {
  min-width: 0;
}
.co-mi-t {
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
  font-size: 13.5px;
}
.co-mi-d {
  display: block;
  color: var(--ink-mute);
  font-size: 12px;
  margin-top: 2px;
  line-height: 1.4;
}
.co-mi-soon {
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #fff;
  background: linear-gradient(135deg, #7c5cd6, #9d86e8);
  border-radius: 5px;
  padding: 2px 5px;
}
</style>
